/**
 * GPON Network Generator
 * Automatically generates splitters, splices, and ONUs based on pole topology
 */
import { Node, Edge } from '@xyflow/react';
import { SplitterType } from '../types';
import { SuggestedSplitter, SuggestedSplice } from '../components/NetworkDesign/DesignPoleNode';

// Fiber colors (standard 12-fiber cable)
const FIBER_COLORS = [
  'Blue', 'Orange', 'Green', 'Brown',
  'Slate', 'White', 'Red', 'Black',
  'Yellow', 'Violet', 'Rose', 'Aqua'
];

// Splitter loss values
const SPLITTER_LOSS: Record<SplitterType, number> = {
  '1:2': 3.5,
  '1:4': 7.0,
  '1:8': 10.5,
  '1:16': 13.5,
  '1:32': 17.0,
  '1:64': 20.5,
};

// Configuration
interface GeneratorConfig {
  preferredSignal: number;  // Preferred signal at ONU (e.g., -15 dBm)
  minSignal: number;        // Minimum acceptable signal (e.g., -28 dBm)
  maxCascadeRatio: number;  // Max total cascade (e.g., 64 for 1:64)
  cableLossPerMeter: number; // Cable attenuation (0.0003 dB/m)
  spliceLoss: number;       // Splice loss (0.1 dB)
}

const DEFAULT_CONFIG: GeneratorConfig = {
  preferredSignal: -15,
  minSignal: -28,
  maxCascadeRatio: 64,
  cableLossPerMeter: 0.0003,
  spliceLoss: 0.1,
};

/**
 * Find optimal splitter type for given number of customers
 */
function findOptimalSplitter(
  customers: number,
  availableSignal: number,
  config: GeneratorConfig
): SplitterType | null {
  // Try splitter types from smallest to largest
  const types: SplitterType[] = ['1:2', '1:4', '1:8', '1:16', '1:32', '1:64'];
  
  for (const type of types) {
    const ratio = parseInt(type.split(':')[1]);
    const loss = SPLITTER_LOSS[type];
    const outputSignal = availableSignal - loss;
    
    // Check if this splitter provides enough ports and acceptable signal
    if (ratio >= customers && outputSignal >= config.minSignal) {
      return type;
    }
  }
  
  return null; // No suitable splitter found
}

/**
 * Calculate cascade ratio from multiple splitters
 */
function calculateCascadeRatio(splitters: SplitterType[]): number {
  return splitters.reduce((total, type) => {
    const ratio = parseInt(type.split(':')[1]);
    return total * ratio;
  }, 1);
}

/**
 * Find path from OLT to pole
 */
function findPathToOLT(
  poleId: string,
  nodes: Node[],
  edges: Edge[]
): { path: string[]; totalLoss: number } | null {
  const visited = new Set<string>();
  const queue: Array<{ nodeId: string; path: string[]; loss: number }> = [];
  
  // Start from pole
  queue.push({ nodeId: poleId, path: [poleId], loss: 0 });
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    
    if (visited.has(current.nodeId)) continue;
    visited.add(current.nodeId);
    
    const node = nodes.find(n => n.id === current.nodeId);
    if (!node) continue;
    
    // Found OLT
    if (node.type === 'olt') {
      return { path: current.path.reverse(), totalLoss: current.loss };
    }
    
    // Find incoming edges
    const incomingEdges = edges.filter(e => e.target === current.nodeId);
    
    for (const edge of incomingEdges) {
      const edgeLoss = (edge.data as any)?.loss || 0;
      queue.push({
        nodeId: edge.source,
        path: [...current.path, edge.source],
        loss: current.loss + edgeLoss,
      });
    }
  }
  
  return null; // No path to OLT found
}

/**
 * Generate actual nodes and edges for network design
 */
export function generateNetworkNodes(
  poles: Node[],
  allNodes: Node[],
  allEdges: Edge[],
  config: GeneratorConfig = DEFAULT_CONFIG
): {
  newNodes: Node[];
  newEdges: Edge[];
  warnings: string[];
} {
  const newNodes: Node[] = [];
  const newEdges: Edge[] = [];
  const warnings: string[] = [];

  let splitterCounter = 0;
  let onuCounter = 0;

  for (const poleNode of poles) {
    const poleData = poleNode.data as any;
    const expectedCustomers = poleData.expectedCustomers || 0;

    if (expectedCustomers === 0) continue;

    // Find path to OLT
    const pathInfo = findPathToOLT(poleNode.id, allNodes, allEdges);

    if (!pathInfo) {
      warnings.push(`${poleData.label}: No connection to OLT found`);
      continue;
    }

    // Get OLT power
    const oltNode = allNodes.find(n => n.id === pathInfo.path[0]);
    const oltPower = (oltNode?.data as any)?.power || 3.0;

    // Calculate available signal at pole
    const availableSignal = oltPower - pathInfo.totalLoss;

    // Find optimal splitter for customers
    const splitterType = findOptimalSplitter(expectedCustomers, availableSignal, config);

    if (!splitterType) {
      warnings.push(`${poleData.label}: Cannot provide signal for ${expectedCustomers} customers (${availableSignal.toFixed(2)} dBm available)`);
      continue;
    }

    // Create Splitter node
    splitterCounter++;
    const splitterNode: Node = {
      id: `generated-splitter-${splitterCounter}`,
      type: 'splitter',
      position: {
        x: poleNode.position.x + 200,
        y: poleNode.position.y - 50,
      },
      data: {
        label: `${poleData.label} Splitter`,
        type: splitterType,
        loss: SPLITTER_LOSS[splitterType],
        inputPower: availableSignal,
      },
    };

    newNodes.push(splitterNode);

    // Create edge from Pole to Splitter
    newEdges.push({
      id: `edge-${poleNode.id}-${splitterNode.id}`,
      source: poleNode.id,
      target: splitterNode.id,
      type: 'design',
      data: {
        distance: 10, // Short distance (in cassette)
        loss: 0.003,
        notes: `Fiber #1 (${FIBER_COLORS[0]}) in cassette`,
      },
    });

    // Create ONU nodes
    const ratio = parseInt(splitterType.split(':')[1]);
    const splitterLoss = SPLITTER_LOSS[splitterType];
    const outputSignal = availableSignal - splitterLoss;

    for (let i = 0; i < expectedCustomers; i++) {
      onuCounter++;
      const onuNode: Node = {
        id: `generated-onu-${onuCounter}`,
        type: 'onu',
        position: {
          x: splitterNode.position.x + 250,
          y: splitterNode.position.y + (i - expectedCustomers / 2) * 80,
        },
        data: {
          label: `${poleData.label} ONU ${i + 1}`,
          expectedCustomers: 1,
          inputPower: outputSignal,
        },
      };

      newNodes.push(onuNode);

      // Create edge from Splitter to ONU
      newEdges.push({
        id: `edge-${splitterNode.id}-${onuNode.id}`,
        source: splitterNode.id,
        sourceHandle: `output-${i}`,
        target: onuNode.id,
        targetHandle: 'input',
        type: 'design',
        data: {
          distance: 50, // Short distance to customer
          loss: 0.015,
          notes: `To customer ${i + 1}`,
        },
      });
    }
  }

  return { newNodes, newEdges, warnings };
}

/**
 * Generate network design for a pole (legacy - for details view)
 */
export function generatePoleNetwork(
  poleNode: Node,
  allNodes: Node[],
  allEdges: Edge[],
  config: GeneratorConfig = DEFAULT_CONFIG
): {
  suggestedSplitters: SuggestedSplitter[];
  suggestedSplices: SuggestedSplice[];
  suggestedONUs: number;
  warnings: string[];
  recommendations: string[];
} {
  const warnings: string[] = [];
  const recommendations: string[] = [];
  const suggestedSplitters: SuggestedSplitter[] = [];
  const suggestedSplices: SuggestedSplice[] = [];

  const poleData = poleNode.data as any;
  const expectedCustomers = poleData.expectedCustomers || 0;
  
  // If no customers, just handle pass-through
  if (expectedCustomers === 0) {
    // Find incoming and outgoing cables
    const incomingEdges = allEdges.filter(e => e.target === poleNode.id);
    const outgoingEdges = allEdges.filter(e => e.source === poleNode.id);
    
    if (incomingEdges.length > 0 && outgoingEdges.length > 0) {
      // Suggest splices for pass-through
      let fiberIndex = 0;
      for (const outEdge of outgoingEdges) {
        if (fiberIndex < 12) {
          suggestedSplices.push({
            id: `splice-${poleNode.id}-${fiberIndex}`,
            fromCable: incomingEdges[0].id,
            toCable: outEdge.id,
            fiber: fiberIndex + 1,
            fiberColor: FIBER_COLORS[fiberIndex],
            loss: config.spliceLoss,
          });
          fiberIndex++;
        }
      }
    }
    
    return {
      suggestedSplitters,
      suggestedSplices,
      suggestedONUs: 0,
      warnings,
      recommendations,
    };
  }
  
  // Find path to OLT
  const pathInfo = findPathToOLT(poleNode.id, allNodes, allEdges);
  
  if (!pathInfo) {
    warnings.push('No connection to OLT found');
    return {
      suggestedSplitters,
      suggestedSplices,
      suggestedONUs: 0,
      warnings,
      recommendations,
    };
  }
  
  // Get OLT power
  const oltNode = allNodes.find(n => n.id === pathInfo.path[0]);
  const oltPower = (oltNode?.data as any)?.power || 3.0;
  
  // Calculate available signal at pole
  const availableSignal = oltPower - pathInfo.totalLoss;
  
  // Find optimal splitter for customers
  const splitterType = findOptimalSplitter(expectedCustomers, availableSignal, config);
  
  if (!splitterType) {
    warnings.push(`Cannot provide signal for ${expectedCustomers} customers`);
    warnings.push(`Available signal: ${availableSignal.toFixed(2)} dBm`);
    recommendations.push('Reduce distance or add intermediate splitter');
    return {
      suggestedSplitters,
      suggestedSplices,
      suggestedONUs: 0,
      warnings,
      recommendations,
    };
  }
  
  // Add splitter for customers
  const splitterLoss = SPLITTER_LOSS[splitterType];
  const outputSignal = availableSignal - splitterLoss;
  const ratio = parseInt(splitterType.split(':')[1]);
  
  suggestedSplitters.push({
    id: `splitter-${poleNode.id}-customers`,
    type: splitterType,
    purpose: 'customers',
    inputFiber: 1, // Use fiber #1 for customers
    outputFibers: Array.from({ length: expectedCustomers }, (_, i) => i + 1),
    loss: splitterLoss,
    note: `In cassette at ${poleData.label}`,
  });
  
  // Check signal quality
  if (outputSignal < config.preferredSignal) {
    warnings.push(`Signal ${outputSignal.toFixed(2)} dBm is below preferred ${config.preferredSignal} dBm`);
  }
  
  if (outputSignal < config.minSignal) {
    warnings.push(`Signal ${outputSignal.toFixed(2)} dBm is below minimum ${config.minSignal} dBm`);
  }
  
  // Handle pass-through for outgoing cables
  const outgoingEdges = allEdges.filter(e => e.source === poleNode.id);
  const incomingEdges = allEdges.filter(e => e.target === poleNode.id);
  
  if (outgoingEdges.length > 0 && incomingEdges.length > 0) {
    // Use remaining fibers for pass-through
    let fiberIndex = 1; // Start from fiber #2 (fiber #1 used for customers)
    
    for (const outEdge of outgoingEdges) {
      if (fiberIndex < 12) {
        suggestedSplices.push({
          id: `splice-${poleNode.id}-${fiberIndex}`,
          fromCable: incomingEdges[0].id,
          toCable: outEdge.id,
          fiber: fiberIndex + 1,
          fiberColor: FIBER_COLORS[fiberIndex],
          loss: config.spliceLoss,
        });
        fiberIndex++;
      } else {
        warnings.push('Not enough fibers for all connections (max 12)');
      }
    }
  }
  
  return {
    suggestedSplitters,
    suggestedSplices,
    suggestedONUs: expectedCustomers,
    warnings,
    recommendations,
  };
}

